import * as React from "react";
import { Search } from "lucide-react";
import { Avatar, Button, Dialog, Input, InputGroup, Skeleton } from "@/components/ui";
import { getInitials } from "@/components/reviews/utils";
import { PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";

export interface PlayerPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  onSelect: (player: PlayerPickerResult) => void;
  excludePlayerIds?: number[];
}

export interface PlayerPickerResult {
  id: number;
  userId: number;
  fullName: string | null;
  email: string | null;
  photoUrl: string | null;
}

type PlayerOption = PlayerPickerResult & { label: string };

const SEARCH_DEBOUNCE_MS = 300;

async function getPlayers(search: string | undefined, page: number) {
  // The default order: last login would be the natural sort, but it isn't recorded reliably yet.
  const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
  return {
    ...result,
    content: result.content.map(
      (player): PlayerOption => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
        fullName: player.fullName,
        email: player.email,
        photoUrl: player.photoUrl,
        userId: player.userId,
      })
    ),
  };
}

// Plain text, not PlayerCell: its name and email are links, which can't sit inside a choice.
const PlayerSummary: React.FC<{ player: PlayerOption }> = ({ player }) => (
  <div className="flex min-w-0 items-center gap-3">
    <Avatar className="size-8 shrink-0">
      {player.photoUrl && <Avatar.Image src={player.photoUrl} alt="" />}
      <Avatar.Fallback className="text-xs font-semibold">{getInitials(player.fullName)}</Avatar.Fallback>
    </Avatar>
    <div className="flex min-w-0 flex-col">
      <span className="truncate text-sm font-bold text-slate-700">{player.fullName || "No Name"}</span>
      {player.email && <span className="truncate text-xs text-slate-500">{player.email}</span>}
    </div>
  </div>
);

const PlayerRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 px-2 py-2" data-testid="player-row-skeleton">
    <Skeleton className="size-8 shrink-0 rounded-full" />
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-48" />
    </div>
  </div>
);

/** Picks the one player a coach is posting for. */
export const PlayerPickerDialog: React.FC<PlayerPickerDialogProps> = ({
  open,
  onOpenChange,
  title = "Who is this post for?",
  description = "Pick one player. We'll use their reviews, branding, and connected accounts.",
  onSelect,
  excludePlayerIds,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange} size="md" showCloseButton>
    <Dialog.Header>
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.Description>{description}</Dialog.Description>
    </Dialog.Header>
    {/* Mounted only while open, so each opening starts from a fresh search and no selection. */}
    {open && (
      <PlayerPickerBody
        excludePlayerIds={excludePlayerIds}
        onContinue={player => {
          onSelect(player);
          onOpenChange(false);
        }}
      />
    )}
  </Dialog>
);

const PlayerPickerBody: React.FC<{
  excludePlayerIds?: number[];
  onContinue: (player: PlayerPickerResult) => void;
}> = ({ excludePlayerIds, onContinue }) => {
  const players = usePaginatedCombobox(getPlayers, false);
  const [search, setSearch] = React.useState("");
  const searchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  const onSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => players.onSearch(value.trim()), SEARCH_DEBOUNCE_MS);
  };

  const selected = players.value ?? null;
  const items = players.items.filter(p => !excludePlayerIds?.includes(p.id));
  const firstLoad = players.isLoadingMore && items.length === 0;
  const listRef = React.useRef<HTMLDivElement>(null);

  const onScroll = () => {
    const el = listRef.current;
    if (!el || !players.hasMore || players.isLoadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) players.onLoadMore();
  };

  const select = (player: PlayerOption | null) => players.onValueChange(player, undefined);

  return (
    <>
      <Dialog.Content className="flex flex-col gap-3">
        {selected ? (
          <div
            className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
            data-testid="selected-player"
          >
            <PlayerSummary player={selected} />
            <Button variant="link" size="sm" className="px-0" onClick={() => select(null)}>
              Change
            </Button>
          </div>
        ) : (
          <>
            <InputGroup
              className="w-full bg-white"
              prepend={<Search className="size-4 text-muted-foreground" />}
              control={controlProps => (
                <Input
                  {...controlProps}
                  autoFocus
                  value={search}
                  placeholder="Search players by name or email"
                  aria-label="Search players by name or email"
                  onChange={event => onSearchChange(event.target.value)}
                />
              )}
            />
            <div
              ref={listRef}
              onScroll={onScroll}
              className="flex max-h-80 flex-col overflow-y-auto"
              role="listbox"
              aria-label="Players"
            >
              {firstLoad ? (
                <>
                  <PlayerRowSkeleton />
                  <PlayerRowSkeleton />
                  <PlayerRowSkeleton />
                </>
              ) : items.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-slate-500">No players match that name.</p>
              ) : (
                items.map(player => (
                  <div
                    key={player.id}
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    aria-label={player.label}
                    onClick={() => select(player)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        select(player);
                      }
                    }}
                    className="cursor-pointer rounded-md px-2 py-2 outline-none hover:bg-slate-50 focus-visible:bg-slate-50"
                  >
                    <PlayerSummary player={player} />
                  </div>
                ))
              )}
              {players.isLoadingMore && items.length > 0 && <PlayerRowSkeleton />}
            </div>
          </>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <div className="flex justify-end">
          <Button disabled={!selected} onClick={() => selected && onContinue(stripLabel(selected))}>
            Continue
          </Button>
        </div>
      </Dialog.Footer>
    </>
  );
};

function stripLabel({ label: _label, ...player }: PlayerOption): PlayerPickerResult {
  return player;
}
